import { observable, runInAction, makeObservable } from 'mobx';
import { hasFHIRLaunchParams, isJwtState } from './helpers/fhirLaunch';
import { TCClient } from 'types';
import type { Params } from 'stores/FeatureStore';

type SessionStoreForLaunch = {
  setProcessingToken: (value: boolean) => void;
};

type Stores = {
  sessionStore?: SessionStoreForLaunch;
};
type LaunchEndpoint = 'launch' | 'pcm' | 'call';

export interface CallResponse {
  email?: string | null;
  firstName: string;
  lastName: string;
  npi?: string | null;
  sysLogin?: string;
  jwt: string;
}

export interface PCMResponse {
  dob: string;
  mrn: string;
  name: string;
  sex: string;
  email?: string | null;
  firstName?: string;
  lastName?: string;
  npi?: string | null;
  jwt?: string;
  sysLogin?: string;
}

export type LaunchData =
  | { type: 'pcm'; data: PCMResponse }
  | { type: 'call'; data: CallResponse }
  | undefined;

export default class LaunchStore {
  client: TCClient;
  stores: Stores;
  params: Params;

  @observable launchServiceFlow = false;
  @observable launchError = false;
  @observable didFHIRLaunchFail = false;
  @observable jwt: string | null = null;
  @observable launchType: Params['launchType'] | undefined = undefined;
  @observable.shallow launchData: LaunchData = undefined;

  constructor({ client, params, stores }: { client: TCClient; params: Params; stores: Stores }) {
    makeObservable(this);
    this.client = client;
    this.params = params;
    this.stores = stores;
  }

  mounted() {
    if (this.isFHIRLaunchSession || this.isFHIRRedirectPending) this.setFhirProcessingState(true);

    if (this.hasAuthorizationCodeParams()) {
      this.exchangeAuthorizationCode();
      return;
    }

    const { iss, launch } = this.fhirParams;
    if (iss && launch) {
      this.launchRequest();
    } else if (this.isFHIRRedirectPending) {
      runInAction(() => {
        console.log('FHIR redirect pending but missing required parameters for launch');
        this.didFHIRLaunchFail = true;
        this.launchError = true;
      });
      this.setFhirProcessingState(false);
    }
  }

  get isFHIRLaunchSession() {
    return hasFHIRLaunchParams(this.fhirParams);
  }

  get isFHIRRedirectPending() {
    const { launchType } = this.fhirParams;
    return Boolean(launchType) && this.hasFHIRIntentParams() && !this.isFHIRLaunchSession;
  }

  async launchRequest() {
    try {
      const { iss, launch, launchType } = this.fhirParams;
      const url = new URL(this.getEndpointUrl('launch'));

      const params = [
        ['type', launchType || 'pcm'],
        ['iss', iss],
        ['launch', launch],
      ].filter((v): v is [string, string] => typeof v[0] === 'string' && typeof v[1] === 'string');

      for (const [key, value] of params) {
        url.searchParams.set(key, value);
      }

      const res = await fetch(url.href);
      if (!res.ok) {
        throw new Error(`FHIR launch request failed with status ${res.status}`);
      }

      const data = await res.json();

      if (this.handleAuthorizeRedirectIfNeeded(data)) return;

      await this.handleLaunchPayload(data, launchType);
    } catch (e) {
      runInAction(() => {
        console.log('Failure to initiate FHIR launch request: ', e);
        this.didFHIRLaunchFail = true;
      });
      this.setFhirProcessingState(false);
    }
  }

  async exchangeAuthorizationCode() {
    try {
      const { code, state, launchType } = this.fhirParams;

      if (!code || !state) {
        this.setFhirProcessingState(false);
        return;
      }

      const url = new URL(this.getEndpointUrl(this.resolveLaunchType(launchType)));
      url.searchParams.set('code', code);
      url.searchParams.set('state', state);
      url.searchParams.set('redirect', 'false');

      const res = await fetch(url.href);
      if (!res.ok) {
        throw new Error(`FHIR authorization exchange failed with status ${res.status}`);
      }

      const data = await res.json();
      await this.handleLaunchPayload(data, launchType);
    } catch (e) {
      runInAction(() => {
        console.log('Failure to exchange FHIR authorization code: ', e);
        this.didFHIRLaunchFail = true;
      });
      this.setFhirProcessingState(false);
    }
  }

  getUrl() {
    return this.getEndpointUrl('launch');
  }

  private hasAuthorizationCodeParams() {
    const { code, state, launchType } = this.fhirParams;
    const hasLaunchType = typeof launchType === 'string' && launchType.length > 0;

    return (
      typeof code === 'string' &&
      code.length > 0 &&
      typeof state === 'string' &&
      state.length > 0 &&
      (hasLaunchType || isJwtState(state))
    );
  }

  private get fhirParams() {
    const { iss, launch, code, state, launchType, launchServiceUrl } = this.params;
    return { iss, launch, code, state, launchType, launchServiceUrl };
  }

  private hasFHIRIntentParams() {
    const { launch, iss } = this.fhirParams;
    return (
      (typeof launch === 'string' && launch.length > 0) ||
      (typeof iss === 'string' && iss.length > 0)
    );
  }

  private getEndpointUrl(endpoint: LaunchEndpoint) {
    if (endpoint === 'launch') return this.getLaunchEndpointUrl();

    const baseUrl = this.getLaunchBaseUrl();
    const normalizedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    return `${normalizedBase}/${endpoint}`;
  }

  private getLaunchEndpointUrl() {
    const { launchServiceUrl } = this.fhirParams;
    return launchServiceUrl || `${this.client.webInfraUrl}/v1/fhir-launch/launch`;
  }

  private getLaunchBaseUrl() {
    const { launchServiceUrl } = this.fhirParams;

    if (!launchServiceUrl) return `${this.client.webInfraUrl}/v1/fhir-launch`;

    try {
      const url = new URL(launchServiceUrl);
      url.hash = '';
      url.search = '';
      const trimmedPath = url.pathname.replace(/\/+$/, '');
      const basePath = trimmedPath.endsWith('/launch')
        ? trimmedPath.slice(0, -'/launch'.length)
        : trimmedPath;
      return `${url.origin}${basePath}`;
    } catch (err) {
      console.log('Unable to parse launchServiceUrl, using provided value as base', err);
      return launchServiceUrl.replace(/\/launch\/?$/, '');
    }
  }

  private resolveLaunchType(type?: Params['launchType']): 'pcm' | 'call' {
    return type === 'call' ? 'call' : 'pcm';
  }

  private handleAuthorizeRedirectIfNeeded(data: unknown) {
    if (!this.isAuthorizeRedirectResponse(data)) return false;

    if (typeof window !== 'undefined') {
      if (typeof window.location?.assign === 'function') {
        try {
          window.location.assign(data.authorizeUrl);
        } catch (err) {
          runInAction(() => {
            console.log('Unable to follow authorizeUrl due to assignment failure', err);
            this.launchError = true;
            this.didFHIRLaunchFail = true;
          });
          this.setFhirProcessingState(false);
          return true;
        }
        this.setFhirProcessingState(false);
      } else {
        runInAction(() => {
          console.log('Missing window.location.assign, unable to follow authorizeUrl');
          this.launchError = true;
          this.didFHIRLaunchFail = true;
        });
        this.setFhirProcessingState(false);
      }
    } else {
      runInAction(() => {
        console.log('Missing window context, unable to follow authorizeUrl');
        this.launchError = true;
        this.didFHIRLaunchFail = true;
      });
      this.setFhirProcessingState(false);
    }
    return true;
  }

  private isAuthorizeRedirectResponse(data: unknown): data is { authorizeUrl: string } {
    return (
      typeof data === 'object' &&
      data !== null &&
      'authorizeUrl' in data &&
      typeof (data as { authorizeUrl?: unknown }).authorizeUrl === 'string'
    );
  }

  private async handleLaunchPayload(
    data: PCMResponse | CallResponse | undefined,
    launchType?: Params['launchType']
  ) {
    const resolvedLaunchType = this.resolveLaunchType(launchType);
    if (!data) {
      runInAction(() => {
        console.log(`Invalid FHIR Launch Response for ${resolvedLaunchType.toUpperCase()}`);
        this.launchError = true;
        this.didFHIRLaunchFail = true;
      });
      this.setFhirProcessingState(false);
      return;
    }

    if (data.jwt) {
      this.jwt = data.jwt;
      await this.client.signInWithJwtToken(data.jwt, { jwt: data.jwt });
      runInAction(() => {
        if (resolvedLaunchType === 'pcm') {
          this.launchData = {
            type: 'pcm',
            data: data as PCMResponse,
          };
        } else {
          this.launchData = {
            type: 'call',
            data: data as CallResponse,
          };
        }
        this.launchServiceFlow = true;
      });
      this.setFhirProcessingState(false);
      return;
    }

    runInAction(() => {
      console.log('JWT missing or invalid in FHIR Launch Response');
      this.didFHIRLaunchFail = true;
    });
    this.setFhirProcessingState(false);
  }

  private setFhirProcessingState(isProcessing: boolean) {
    this.stores.sessionStore?.setProcessingToken(isProcessing);
  }
}
